import React, { useState, useEffect } from "react";
import ModernChatWrapper from "../../../../../components/organisms/ModernChatWrapper";
import { useRecoilValue } from "recoil";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Card,
  CardHeader,
  CircularProgress,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  PriorityHigh as PriorityIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Message as MessageIcon,
} from "@mui/icons-material";
import moment from "moment";

import {
  StyledVideosCard,
  VideosHeader,
  VideosContent,
  ActionButton,
} from "./StyledComponents";
import SharedVideo from "./SharedVideo";
import EmptyProcessedVideos from "./EmptyProcessedVideos";

import organizationIdSelector from "../../../../../atoms/organizationIdSelector";
import useClientMessageGroupId from "../../../../../hooks/useClientMessageGroupId";
import StandardButton from "../../../../../components/StandardButton";

// Wrapper component to handle groupId for each client
const ClientMessagesWrapper = ({
  uid,
  organizationId,
  height = 400,
  inModal = false,
}) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const groupId = useClientMessageGroupId({ uid, organizationId });

  // Use useEffect to handle the initialization state
  useEffect(() => {
    if (groupId) {
      // Give the chat component a moment to initialize properly
      const timer = setTimeout(() => {
        setIsInitializing(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [groupId]);

  if (!groupId || isInitializing) {
    return (
      <Box
        sx={{
          height: inModal ? "100%" : height,
          border: inModal ? "none" : 1,
          borderColor: "divider",
          borderRadius: inModal ? 0 : 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: inModal ? "transparent" : "background.paper",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          {!groupId ? "Loading conversation..." : "Initializing chat..."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: inModal ? "100%" : height,
        border: inModal ? "none" : 1,
        borderColor: "divider",
        borderRadius: inModal ? 0 : 2,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: inModal ? "transparent" : "background.paper",
      }}
    >
      <ModernChatWrapper
        key={`${uid}-${groupId}`}
        uid={uid}
        groupId={groupId}
        embedded={true}
        height={inModal ? "100%" : "400px"}
        sx={{
          opacity: 0,
          animation: "fadeIn 0.3s ease-in-out forwards",
          "@keyframes fadeIn": {
            "0%": {
              opacity: 0,
              transform: "translateY(10px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      />
    </Box>
  );
};

const VideoGallery = ({
  videos = [],
  unprocessedVideos = [],
  sortMode = "unprocessed",
  onSortModeChange,
  onVideoClick,
  thumbnails = {},
  users = [],
  onProcessAllClientVideos,
  onSendMessage,
  events,
  onRefreshVideos,
  onSeeProcessedVideos,
  allVideos = [],
}) => {
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedClientForChat, setSelectedClientForChat] = useState(null);
  const organizationId = useRecoilValue(organizationIdSelector);

  // Find user by uid
  const findUser = (uid) => {
    return users.find((user) => user.uid === uid);
  };

  return (
    <StyledVideosCard>
      <VideosHeader>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ width: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PriorityIcon color="warning" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Priority Queue
            </Typography>
            {unprocessedVideos.length > 0 && (
              <Chip
                label={`${unprocessedVideos.length} pending review`}
                color="warning"
                size="small"
              />
            )}
          </Stack>
          <ActionButton
            variant="outlined"
            size="small"
            sx={{ minWidth: { xs: "auto", sm: 140 } }}
            onClick={() => {
              // Toggle sorting mode
              if (sortMode === "unprocessed") onSortModeChange("oldest");
              else if (sortMode === "oldest") onSortModeChange("newest");
              else onSortModeChange("unprocessed");
            }}
            startIcon={<ScheduleIcon />}
          >
            {sortMode === "unprocessed"
              ? "Priority"
              : sortMode === "oldest"
                ? "Oldest"
                : "Newest"}
          </ActionButton>
        </Stack>
      </VideosHeader>

      <VideosContent>
        {/* Show empty state when there are no unprocessed videos to display */}
        {sortMode === "unprocessed" && unprocessedVideos.length === 0 ? (
          <EmptyProcessedVideos 
            onRefreshVideos={onRefreshVideos}
            onSeeProcessedVideos={onSeeProcessedVideos}
            totalVideosCount={allVideos.length}
          />
        ) : (
          videos.map((dateGroup) => {
          // Filter out date groups with only processed videos if in unprocessed mode
          const hasUnprocessedInDate = dateGroup.hours.some((hour) =>
            hour.clients.some((client) =>
              client.videos.some((video) => !video.processed),
            ),
          );

          if (sortMode === "unprocessed" && !hasUnprocessedInDate) {
            return null;
          }

          return (
            <Box key={dateGroup.date} sx={{ mb: 2 }}>
              <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                {dateGroup.date}
              </Typography>

              {dateGroup.hours.map((hour) => {
                // Filter out hours with only processed videos if in unprocessed mode
                const hasUnprocessedInHour = hour.clients.some((client) =>
                  client.videos.some((video) => !video.processed),
                );

                if (sortMode === "unprocessed" && !hasUnprocessedInHour) {
                  return null;
                }

                // Convert hour to 12-hour format
                const hour12 = moment(hour.hour, ["HH"]).format("h A");

                return (
                  <Box key={`${dateGroup.date}_${hour.hour}`} sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {hour12}
                    </Typography>

                    {hour.clients.map((client) => {
                      // Filter client videos based on sort mode
                      let clientVideos = [...client.videos];

                      if (sortMode === "unprocessed") {
                        // Show only unprocessed videos
                        clientVideos = clientVideos.filter((v) => !v.processed);

                        // If no unprocessed videos for this client, skip
                        if (clientVideos.length === 0) return null;
                      }

                      // Sort videos for this client by date (oldest first)
                      clientVideos.sort((a, b) => {
                        return a.createdOn.toDate() - b.createdOn.toDate();
                      });

                      const user = findUser(client.clientUID);
                      const userName = user
                        ? `${user.firstName} ${user.lastName}`
                        : "";

                      return (
                        <Paper
                          key={`${dateGroup.date}_${hour.hour}_${client.clientUID}`}
                          sx={{
                            p: 2,
                            mb: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", sm: "center" },
                              gap: { xs: 1, sm: 0 },
                              mb: 1.5,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ flexWrap: "wrap" }}
                            >
                              <PersonIcon color="primary" />
                              <Typography variant="subtitle1" fontWeight="bold">
                                {userName}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${clientVideos.length} videos`}
                                color={
                                  clientVideos.some((v) => !v.processed)
                                    ? "warning"
                                    : "success"
                                }
                              />
                            </Stack>

                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              <StandardButton
                                size="small"
                                variant="secondary"
                                sx={{
                                  width: { xs: "100%", sm: "auto" },
                                  minWidth: { sm: 120 },
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => {
                                  console.log(
                                    "Opening chat modal for client:",
                                    client.clientUID,
                                  );
                                  setSelectedClientForChat(client);
                                  setChatModalOpen(true);
                                  events?.logButtonClick({
                                    buttonName: "Send Message",
                                    pageName: "Shared Videos",
                                  });
                                }}
                              >
                                Send Message
                              </StandardButton>

                              <StandardButton
                                size="small"
                                variant="primary"
                                sx={{
                                  width: { xs: "100%", sm: "auto" },
                                  minWidth: { sm: 140 },
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() =>
                                  onProcessAllClientVideos(
                                    client.clientUID,
                                    clientVideos.filter((v) => !v.processed),
                                  )
                                }
                                disabled={
                                  !clientVideos.some((v) => !v.processed)
                                }
                              >
                                Mark All Processed
                              </StandardButton>
                            </Stack>
                          </Box>

                          <Grid container spacing={1.5}>
                            {clientVideos.map((video) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={video.id}
                              >
                                <SharedVideo
                                  video={video}
                                  onClick={onVideoClick}
                                  thumbnail={thumbnails[video.id]}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          );
          })
        )}
      </VideosContent>

      {/* Chat Modal Dialog */}
      <Dialog
        open={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            Messages with{" "}
            {selectedClientForChat
              ? findUser(selectedClientForChat.clientUID)?.displayName ||
                "Client"
              : ""}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          {selectedClientForChat && (
            <ClientMessagesWrapper
              key={`modal-messages-${selectedClientForChat.clientUID}`}
              uid={selectedClientForChat.clientUID}
              organizationId={organizationId}
              inModal={true}
            />
          )}
        </DialogContent>
        <DialogActions>
          <StandardButton variant="secondary" onClick={() => setChatModalOpen(false)}>
            Close
          </StandardButton>
        </DialogActions>
      </Dialog>
    </StyledVideosCard>
  );
};

export default VideoGallery;
